import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, interval, Subject, Subscription, timer } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { MaterialModule } from 'src/app/material.module';
import { AbdmConsentRequestDialogComponent } from './abdm-consent-request-dialog.component';
import { AbdmAttachment, toAttachmentBlob } from './abdm-fhir-attachments';
import { AbdmFhirView, formatFhirJson, presentFhirBundle } from './abdm-fhir-presenter';
import { AbdmHealthRecordsService } from './abdm-health-records.service';
import { AbdmHiuConsent, AbdmHiuRecord } from './abdm-health-records.type';
import { CommonDatePipe } from 'src/app/pipe/common-date.pipe';

const ATTACHMENT_URL_LIFETIME_MS = 60000;
// NHA HIU_FLOW_301: records must leave the screen when the consent expires, without a reload.
const BACKGROUND_REFRESH_MS = 30000;
const EXPIRY_GRACE_MS = 2000;
const MAX_TIMER_MS = 24 * 60 * 60 * 1000;

@Component({
  selector: 'app-abdm-health-records',
  standalone: true,
  imports: [CommonModule, MaterialModule, TranslateModule, CommonDatePipe],
  templateUrl: './abdm-health-records.component.html',
  styleUrl: './abdm-health-records.component.scss'
})
export class AbdmHealthRecordsComponent implements OnInit, OnDestroy {
  @Input({ required: true }) patientId!: number;
  @Input() abhaAddress?: string | null;
  @Input() abhaNumber?: string | null;
  consents: AbdmHiuConsent[] = [];
  records: AbdmHiuRecord[] = [];
  clinicalViews = new Map<number, AbdmFhirView>();
  detailLoadFailures = new Set<number>();
  readonly fhirVisibleRecords = new Set<number>();
  loading = false;
  pendingFetch = false;
  searchTerm = '';
  currentPage = 1;
  readonly pageSize = 10;
  readonly expandedConsentTypes = new Set<number>();

  private readonly destroy$ = new Subject<void>();
  private readonly recoveryInFlight = new Set<number>();
  private expiryTimer?: Subscription;

  constructor(
    private service: AbdmHealthRecordsService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.refresh();
    interval(BACKGROUND_REFRESH_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refresh(true));
  }

  ngOnDestroy(): void {
    this.expiryTimer?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get filteredRecords(): AbdmHiuRecord[] {
    const search = this.searchTerm.trim().toLowerCase();
    if (!search) {
      return this.records;
    }

    return this.records.filter(record => [
      record.title,
      record.hiType,
      record.sourceHipId,
      record.careContextReference
    ].some(value => value?.toLowerCase().includes(search)));
  }

  get pagedRecords(): AbdmHiuRecord[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredRecords.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredRecords.length / this.pageSize));
  }

  get visiblePages(): number[] {
    const start = Math.max(1, Math.min(this.currentPage - 1, this.totalPages - 3));
    const end = Math.min(this.totalPages, start + 3);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  get firstVisibleRecord(): number {
    return this.filteredRecords.length ? (this.currentPage - 1) * this.pageSize + 1 : 0;
  }

  get lastVisibleRecord(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredRecords.length);
  }

  // A silent refresh runs in the background: no progress bar, no toast, page kept.
  refresh(silent = false): void {
    if (this.loading) {
      return;
    }
    this.loading = !silent;
    forkJoin({
      consents: this.service.getConsents(this.patientId, silent),
      records: this.service.getRecords(this.patientId, silent)
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: response => this.applyRefresh(response.consents.result || [], response.records.result || [], silent),
      error: (error) => this.handleRefreshError(error, silent)
    });
  }

  private applyRefresh(consents: AbdmHiuConsent[], records: AbdmHiuRecord[], silent: boolean): void {
    this.consents = consents;
    this.records = records.filter(record => !this.isErased(record));
    this.pruneDetailState();
    this.currentPage = silent ? Math.min(this.currentPage, this.totalPages) : 1;
    this.loading = false;
    this.pendingFetch = this.consents.some(consent => this.isFetchingRecords(consent));
    this.scheduleExpiryRefresh();
  }

  private handleRefreshError(error: unknown, silent: boolean): void {
    this.loading = false;
    if (silent) {
      console.warn('ABDM health records background refresh failed', error);
      return;
    }

    this.toastr.error(this.resolveRefreshError(error));
  }

  private resolveRefreshError(error: unknown): string {
    const body = (error as { error?: { message?: string; errors?: string[] } }).error;
    if (body && body.message) {
      return body.message;
    }

    if (body && body.errors && body.errors.length > 0) {
      return body.errors[0];
    }

    return this.translate.instant('ABDM.REFRESH_FAILED');
  }

  private isErased(record: AbdmHiuRecord): boolean {
    return new Date(record.dataEraseAt).getTime() <= Date.now();
  }

  // Drops records whose erase time has passed and the detail state that belonged to them.
  private dropErasedRecords(): void {
    const remaining = this.records.filter(record => !this.isErased(record));
    if (remaining.length === this.records.length) {
      return;
    }

    this.records = remaining;
    this.pruneDetailState();
    this.currentPage = Math.min(this.currentPage, this.totalPages);
  }

  private pruneDetailState(): void {
    const liveIds = new Set(this.records.map(record => record.id));
    for (const id of [...this.clinicalViews.keys()]) {
      if (!liveIds.has(id)) {
        this.clinicalViews.delete(id);
        this.fhirVisibleRecords.delete(id);
        this.detailLoadFailures.delete(id);
      }
    }
  }

  // One timer for the next expiry, so the screen flips at the right minute instead of
  // up to one polling interval late.
  private scheduleExpiryRefresh(): void {
    this.expiryTimer?.unsubscribe();
    const nextExpiry = this.nextExpiryTime();
    if (nextExpiry === null) {
      return;
    }

    const delay = Math.min(Math.max(nextExpiry - Date.now(), 0) + EXPIRY_GRACE_MS, MAX_TIMER_MS);
    this.expiryTimer = timer(delay).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.dropErasedRecords();
      this.refresh(true);
    });
  }

  private nextExpiryTime(): number | null {
    const now = Date.now();
    const times = [
      ...this.records.map(record => new Date(record.dataEraseAt).getTime()),
      ...this.consents.filter(consent => consent.status === 'Granted').map(consent => new Date(consent.dataEraseAt).getTime())
    ].filter(time => !Number.isNaN(time) && time > now);
    return times.length ? Math.min(...times) : null;
  }

  requestRecords(): void {
    this.dialog.open(AbdmConsentRequestDialogComponent, {
      width: '560px',
      data: { patientId: this.patientId, abhaAddress: this.abhaAddress, abhaNumber: this.abhaNumber }
    }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe(created => {
      if (created) {
        this.refresh();
      }
    });
  }

  updateSearch(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
    this.currentPage = 1;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
  }

  visibleHiTypes(consent: AbdmHiuConsent): string[] {
    return this.expandedConsentTypes.has(consent.id) ? consent.hiTypes : consent.hiTypes.slice(0, 3);
  }

  toggleHiTypes(consentId: number): void {
    if (this.expandedConsentTypes.has(consentId)) {
      this.expandedConsentTypes.delete(consentId);
    } else {
      this.expandedConsentTypes.add(consentId);
    }
  }

  hiTypeLabel(hiType: string): string {
    const key = 'ABDM.DATA_TYPE_NAMES.' + hiType;
    const label = this.translate.instant(key);
    return label === key
      ? hiType.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2').replace(/([a-z])([A-Z])/g, '$1 $2')
      : label;
  }

  readableFailureReason(reason: string): string {
    return /\b(?:hip|provider)\b.*(?:not|n't).*acknowledg.*consent/i.test(reason)
      ? this.translate.instant('ABDM.PROVIDER_ACKNOWLEDGEMENT_PENDING')
      : reason;
  }

  statusKey(status: string): string {
    return `ABDM.STATUS.${status.replace(/[^a-zA-Z]/g, '_').toUpperCase()}`;
  }

  statusLabel(status: string): string {
    const key = this.statusKey(status);
    const translated = this.translate.instant(key);
    return translated === key
      ? status.replace(/([a-z])([A-Z])/g, '$1 $2')
      : translated;
  }
  statusClass(status: string): string {
    return status
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/[^a-zA-Z]+/g, '-')
      .toLowerCase();
  }

  getStatusIcon(status: string): string {
    const s = status.toLowerCase();
    if (s.includes('granted') || s.includes('complete')) {
      return 'check_circle';
    }
    if (s.includes('fail') || s.includes('deny') || s.includes('reject') || s.includes('revoke')) {
      return 'cancel';
    }
    return 'schedule';
  }

  recordIcon(hiType: string): string {
    const type = hiType.replace(/[\s_-]/g, '').toLowerCase();
    if (type.includes('discharge')) {
      return 'description';
    }
    if (type.includes('prescription')) {
      return 'medication';
    }
    if (type.includes('diagnostic')) {
      return 'clinical_notes';
    }
    return 'stethoscope';
  }

  retry(consent: AbdmHiuConsent): void {
    if (!this.beginRecovery(consent.id)) {
      return;
    }
    this.service.retryConsent(consent.id).pipe(
      finalize(() => this.endRecovery(consent.id)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('ABDM.RETRY_SUCCESS'));
        this.refresh();
      },
      error: () => this.toastr.error(this.translate.instant('ABDM.RETRY_FAILED'))
    });
  }

  canCheckStatus(consent: AbdmHiuConsent): boolean {
    return ['Requested', 'InitSent'].includes(consent.status) && !!consent.consentRequestId;
  }

  canRetryFetch(consent: AbdmHiuConsent): boolean {
    return consent.status === 'Granted'
      && ['NotStarted', 'Failed'].includes(consent.dataFetchStatus);
  }

  isRecovering(consentId: number): boolean {
    return this.recoveryInFlight.has(consentId);
  }

  checkConsentStatus(consent: AbdmHiuConsent): void {
    if (!this.beginRecovery(consent.id)) {
      return;
    }
    this.service.checkConsentStatus(consent.id).pipe(
      finalize(() => this.endRecovery(consent.id)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: response => {
        if (response.result) {
          Object.assign(consent, response.result);
        }
        this.refresh();
      },
      error: () => this.toastr.error(this.translate.instant('ABDM.STATUS_CHECK_FAILED'))
    });
  }

  retryFetch(consent: AbdmHiuConsent): void {
    if (!this.beginRecovery(consent.id)) {
      return;
    }
    this.pendingFetch = true;
    this.service.fetchConsent(consent.id).pipe(
      finalize(() => this.endRecovery(consent.id)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: response => {
        if (response.result?.dataFetchStatus === 'Failed') {
          this.toastr.error(this.translate.instant('ABDM.FETCH_FAILED'));
        }
        this.refresh();
      },
      error: () => {
        this.pendingFetch = false;
        this.toastr.error(this.translate.instant('ABDM.FETCH_FAILED'));
      }
    });
  }

  private beginRecovery(consentId: number): boolean {
    if (this.recoveryInFlight.has(consentId)) {
      return false;
    }
    this.recoveryInFlight.add(consentId);
    return true;
  }

  private endRecovery(consentId: number): void {
    this.recoveryInFlight.delete(consentId);
  }

  // True only while records are on their way. A request still waiting for the patient
  // to approve shows its own status in the consent table and needs no banner.
  private isFetchingRecords(consent: AbdmHiuConsent): boolean {
    if (consent.status === 'FetchPending') {
      return true;
    }
    return consent.status === 'Granted' && ['NotStarted', 'Pending'].includes(consent.dataFetchStatus);
  }

  loadDetails(record: AbdmHiuRecord): void {
    if (record.fhirJson) {
      this.attachClinicalView(record);
      return;
    }
    this.service.getRecord(record.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        Object.assign(record, response.result);
        this.attachClinicalView(record);
      },
      error: () => {
        this.detailLoadFailures.add(record.id);
        this.toastr.error(this.translate.instant('ABDM.DETAIL_LOAD_FAILED'));
      }
    });
  }

  parseSummary(summary: string): string {
    try {
      const value = JSON.parse(summary);
      const resourceLabel = this.translate.instant('ABDM.FHIR_RESOURCES_COUNT', { count: value.resourceCount });
      return [value.title, resourceLabel, ...(value.highlights || [])].filter(Boolean).join(' | ');
    }
    catch { return summary; }
  }

  toggleFhir(record: AbdmHiuRecord): void {
    if (this.fhirVisibleRecords.has(record.id)) {
      this.fhirVisibleRecords.delete(record.id);
      return;
    }
    this.fhirVisibleRecords.add(record.id);
  }

  formatFhir(fhirJson: string): string {
    return formatFhirJson(fhirJson);
  }

  openAttachment(attachment: AbdmAttachment): void {
    const url = URL.createObjectURL(toAttachmentBlob(attachment));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), ATTACHMENT_URL_LIFETIME_MS);
  }

  private attachClinicalView(record: AbdmHiuRecord): void {
    if (!record.fhirJson || this.clinicalViews.has(record.id)) {
      return;
    }
    const view = presentFhirBundle(record.fhirJson);
    if (view) {
      this.clinicalViews.set(record.id, view);
      this.detailLoadFailures.delete(record.id);
      return;
    }
    this.detailLoadFailures.add(record.id);
  }
}
